import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  BookOpen, 
  MessageSquare, 
  BarChart3, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Ban, 
  Shield, 
  ShieldOff,
  RefreshCw,
  Globe,
  Loader2
} from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import AutoPoster from './AutoPoster';
import UserModeration from './UserModeration';
import CommentModeration from './CommentModeration';
import Dashboard from './Dashboard';

const AdminPortal = () => {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'autoposter', label: 'Auto-Poster', icon: Globe },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'comments', label: 'Comments', icon: MessageSquare },
    { id: 'monuments', label: 'Monuments', icon: BookOpen },
  ];

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get('/api/admin/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard stats={stats} onRefresh={fetchDashboardStats} />;
      case 'autoposter':
        return <AutoPoster />;
      case 'users':
        return <UserModeration />;
      case 'comments':
        return <CommentModeration />;
      case 'monuments':
        return <MonumentManagement />;
      default:
        return <Dashboard stats={stats} onRefresh={fetchDashboardStats} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Admin Header */}
      <div className="glass-morphism border-b border-glass-border">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-serif font-bold text-text-primary">
                Admin Control Center
              </h1>
              <p className="text-text-secondary">
                Welcome back, {user?.username}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="glass-morphism px-4 py-2 rounded-lg">
                <span className="text-sm text-text-secondary">Admin Level:</span>
                <span className="ml-2 text-accent-primary font-medium">
                  {user?.role}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="glass-morphism border-b border-glass-border sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-1 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-all duration-300 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-accent-primary text-accent-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderTabContent()}
        </motion.div>
      </div>
    </div>
  );
};

// Monument Management Component
const MonumentManagement = () => {
  const [monuments, setMonuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    fetchMonuments();
  }, [page, searchQuery]);

  const fetchMonuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', '20');
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`/api/admin/monuments?${params}`);
      setMonuments(response.data.monuments);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch monuments:', error);
      toast.error('Failed to load monuments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (monumentId) => {
    if (!window.confirm('Are you sure you want to delete this monument? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/monuments/${monumentId}`);
      setMonuments(prev => prev.filter(m => m._id !== monumentId));
      toast.success('Monument deleted successfully');
    } catch (error) {
      console.error('Failed to delete monument:', error);
      toast.error('Failed to delete monument');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-serif font-bold text-text-primary">
          Monument Management
        </h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search monuments..."
              className="pl-10 pr-4 py-2 glass-morphism rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
            />
          </div>
          <button
            onClick={() => window.location.href = '/admin/control-center?tab=autoposter'}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Monument</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
        </div>
      ) : monuments.length === 0 ? (
        <div className="text-center py-20">
          <BookOpen className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h3 className="text-xl font-serif font-semibold text-text-primary mb-2">
            No monuments found
          </h3>
          <p className="text-text-secondary mb-6">
            Get started by adding your first monument using the Auto-Poster.
          </p>
          <button
            onClick={() => window.location.href = '/admin/control-center?tab=autoposter'}
            className="btn-primary"
          >
            Add First Monument
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {monuments.map((monument) => (
            <motion.div
              key={monument._id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-card"
            >
              <div className="relative h-48 mb-4 overflow-hidden rounded-lg">
                {monument.mainImage ? (
                  <img
                    src={monument.mainImage}
                    alt={monument.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-accent-primary/20 to-accent-secondary/20 flex items-center justify-center">
                    <BookOpen className="w-12 h-12 text-accent-primary" />
                  </div>
                )}
              </div>
              
              <h3 className="text-lg font-serif font-semibold text-text-primary mb-2">
                {monument.title}
              </h3>
              
              <div className="flex items-center justify-between text-sm text-text-secondary mb-4">
                <span>{monument.era}</span>
                <span>{monument.likes} likes</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => window.location.href = `/story/${monument.slug}`}
                  className="flex-1 btn-secondary text-sm py-2"
                >
                  View
                </button>
                <button
                  onClick={() => window.location.href = `/admin/control-center?tab=autoposter&edit=${monument._id}`}
                  className="flex-1 btn-secondary text-sm py-2"
                >
                  <Edit className="w-4 h-4 inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(monument._id)}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center space-x-2 mt-8">
          <button
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="p-2 glass-morphism rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-text-secondary">
            Page {page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage(prev => Math.min(pagination.pages, prev + 1))}
            disabled={page === pagination.pages}
            className="p-2 glass-morphism rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminPortal;
